import { TenantViewModel, UserInputModel, UserViewModel } from '@ail/customers-api'
import { DistributionChannel } from '@ail/notifications-api'
import { ReportAdminViewModel, Visibility } from '@ail/reports-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory, useLocation, useParams } from 'react-router-dom'
import IdIsCopied from '../../../../helpers/IdIsCopied'
import { useAuth } from '../../../../hooks/useAuth'
import {
  useDeleteUserMutation,
  useGetTenantsQuery,
  useGetUserQuery,
  useGetUserTagsQuery,
  usePostUserMutation,
  usePutUserMutation,
} from '../../../../redux/api/customerApi'
import { useGetOtherUserSettingsQuery, usePutOtherUserSettingsMutation } from '../../../../redux/api/notificationsApi'
import { isDocumentReportAdmin, useGetReportsAdminQuery } from '../../../../redux/api/reportsApi'
import { newItemId } from '../../../../types/constants'
import { PermissionsEnum } from '../../../../types/enums'
import Alert from '../../../common/Alert/Alert'
import Confirm from '../../../common/Confirm/Confirm'
import { LoadingSpinner } from '../../../common/LoadingSpinner/LoadingSpinner'
import { AssignCustomers } from './AssignCustomers/AssignCustomers'
import { AssignTenants } from './AssignTenants/AssignTenants'
import { NotificationSettings } from './NotificationSettings/NotificationSettings'
import {
  ALL_CHANNELS,
  buildSettingsInputModel,
  DefaultChannels,
  NotificationChannelsState,
} from './NotificationSettings/NotificationSettingsUtils'
import './UserEdit.scss'
import RolesAndPrivilegies from './RolesAndPrivilegies/RolesAndPrivilegies'

const DefaultUser: UserInputModel = {
  enabled: false,
  name: '',
  lastName: '',
  addresses: [],
  contacts: [],
  isManager: false,
  isSupplier: false,
  isGuest: false,
  isCustomer: false,
  requiresCodeAuthentication: false,
  requiresPasswordAuthentication: true,
}

export function UserEdit() {
  const [editingUser, setEditingUser] = useState<UserInputModel>(DefaultUser)
  const [originalEditingUser, setOriginalEditingUser] = useState<UserInputModel>(DefaultUser)
  const [confirmDelete, setConfirmDelete] = useState<UserViewModel>()
  const [validation, setValidation] = useState<Partial<Record<keyof UserInputModel, string>>>({})
  const [deleteAlert, setDeleteAlert] = useState<{ title: string; message: string }>()
  const [saveError, setSaveError] = useState(false)
  const [editingTag, setEditingTag] = useState('')
  const [idCopied, setIdCopied] = useState(false)
  const { user: currentUser } = useAuth()
  const location = useLocation()
  const params = useParams<{ id: string; tenant?: string }>()
  const history = useHistory()
  const isDuplicate = location.pathname.includes('duplicate')
  const isNewUser = useMemo(() => params.id === newItemId, [params.id])
  const existingUser = useGetUserQuery(
    params.tenant && !isNewUser ? { tenantId: params.tenant, userId: params.id } : skipToken
  )
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>()
  const { currentData: reports } = useGetReportsAdminQuery()
  const { currentData: tenants } = useGetTenantsQuery()
  useEffect(() => {
    setSelectedTenantId(isNewUser ? params.tenant : existingUser.currentData?.tenantId)
  }, [existingUser.currentData?.tenantId, isNewUser, params.tenant])
  const userTenant = useMemo(() => {
    return tenants?.find((t) => selectedTenantId && t.id === selectedTenantId)
  }, [selectedTenantId, tenants])
  const userTags = useGetUserTagsQuery(userTenant ? userTenant.id : skipToken)
  const { currentData: userNotificationsSettings } = useGetOtherUserSettingsQuery(params.id ?? skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const { t } = useTranslation('common')
  const [deleteUser, deleteUserResult] = useDeleteUserMutation()
  const [postUser, postUserResult] = usePostUserMutation()
  const [putUser, putUserResult] = usePutUserMutation()
  const [putNotificationSettings, putNotificationSettingsResult] = usePutOtherUserSettingsMutation()

  const assignmentMode = userTenant
    ? userTenant.isSupplier || editingUser.isSupplier
      ? 'tenants'
      : 'customers'
    : undefined

  const loading =
    deleteUserResult.isLoading ||
    postUserResult.isLoading ||
    putUserResult.isLoading ||
    putNotificationSettingsResult.isLoading

  //Notification settings
  const [channels, setChannels] = useState<NotificationChannelsState>(DefaultChannels)
  const [originalChannels, setOriginalChannels] = useState<NotificationChannelsState>(DefaultChannels)

  useEffect(() => {
    if (!userNotificationsSettings) return
    const seeded: NotificationChannelsState = {
      enabledChannelsForApprovalProcessesNotifications:
        userNotificationsSettings.enabledChannelsForApprovalProcessesNotifications ?? [],
      enabledChannelsForRequestsNotifications: userNotificationsSettings.enabledChannelsForRequestsNotifications ?? [],
      enabledChannelsForReportsNotifications: userNotificationsSettings.enabledChannelsForReportsNotifications ?? [],
    }
    setChannels(seeded)
    setOriginalChannels(seeded)
  }, [userNotificationsSettings])

  //
  //! notif update should be skipped if there are no settings at all. meansing user most likely does not have access to netifs
  useEffect(() => {
    if (params.id === newItemId) {
      const newUser = userTenant
        ? { ...DefaultUser, isCustomer: userTenant.isCompany, isSupplier: userTenant.isSupplier }
        : DefaultUser
      setEditingUser(newUser)
      setOriginalEditingUser(newUser)
    } else if (existingUser.currentData) {
      const u: UserInputModel = {
        ...existingUser.currentData,
        tenants: existingUser.currentData.tenants.map((t) => t.id),
        customers: existingUser.currentData.customers.map((t) => t.id),
        units: existingUser.currentData.units.map((t) => t.id),
        ...(isDuplicate && {
          name: '',
          lastName: '',
          firstName: '',
          externalReference: '',
        }),
      }
      setEditingUser(u)
      setOriginalEditingUser(u)
    }
  }, [existingUser.currentData, isDuplicate, params.id, userTenant])

  useEffect(() => {
    setValidation({})
  }, [editingUser, userTenant])

  const userChanged = useMemo(
    () => JSON.stringify(originalEditingUser) !== JSON.stringify(editingUser),
    [editingUser, originalEditingUser]
  )
  const channelsChanged = useMemo(
    () => !!currentUser?.isAdmin && JSON.stringify(originalChannels) !== JSON.stringify(channels),
    [currentUser?.isAdmin, channels, originalChannels]
  )
  const userEdited = userChanged || channelsChanged

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setIdCopied(true)
    setTimeout(() => setIdCopied(false), 2000)
  }

  //toggles multiple settings at once, business logic related
  const enableRelatedSettings = (current: NotificationChannelsState, permission: PermissionsEnum, enabled: boolean) => {
    const value: DistributionChannel[] = enabled ? ALL_CHANNELS : []
    const next: NotificationChannelsState = { ...current }

    if (permission === PermissionsEnum.RequestsApprove) {
      next.enabledChannelsForRequestsNotifications = value
      next.enabledChannelsForApprovalProcessesNotifications = value
    }
    if (permission === PermissionsEnum.ReportsView) {
      next.enabledChannelsForReportsNotifications = value
    }
    setChannels(next)
  }
  const togglePermission = (enabled: boolean, permission: PermissionsEnum) => {
    let permissions = editingUser.permissions ?? []
    if (enabled) {
      permissions = permissions.includes(permission) ? permissions : [...permissions, permission]
    } else {
      permissions = permissions.filter((p) => p !== permission)
    }
    const updatedUser: UserInputModel = {
      ...editingUser,
      permissions,
    }
    setEditingUser(updatedUser)
    enableRelatedSettings(channels, permission, enabled)
  }
  const toggleGuestUserRole = (enabled: boolean) => {
    let user = {
      ...editingUser,
      isGuest: enabled,
      requiresPasswordAuthentication: !enabled,
      requiresCodeAuthentication: enabled,
      permissions: enabled ? [] : originalEditingUser.permissions,
    }

    if (enabled) {
      user = { ...user, isManager: false }
    }
    setEditingUser(user)
  }

  function toggleManager(enabled: boolean) {
    let user = {
      ...editingUser,
      isManager: enabled,
      requiresPasswordAuthentication: enabled,
      requiresCodeAuthentication: !enabled,
    }
    if (enabled) {
      // when toggling, check if user was set to guest previously, if yes, restore original permissions
      // toggle to guest clears permissions for editingUser, without this block permissions would be reset
      // after toggle guest => toggle manager
      user = { ...user, isGuest: false, permissions: user.isGuest ? originalEditingUser.permissions : user.permissions }
    }
    setEditingUser(user)
  }

  const addTag = (newTag: string) => {
    let tags = editingUser.tags ?? []
    if (!tags.some((t) => t.toLowerCase() === newTag.toLowerCase())) {
      tags = [...tags, newTag.toLowerCase()]
      setEditingUser({
        ...editingUser,
        tags,
      })
    }
  }

  const renderUserReports = ({ defaultReports = false, sliders = false } = {}) => {
    if (!userTenant || !reports) {
      return []
    }

    const reportsUnderSelectedTenant = reports.filter((r) => r.tenantId && r.tenantId === userTenant.id)
    const reportAssignedToUser = defaultReports
      ? reportsUnderSelectedTenant.filter((r) => r.visibility === Visibility.Public && !r.allowedUsers?.length)
      : reportsUnderSelectedTenant.filter(
          (r) => r.visibility === Visibility.Public && (r.allowedUsers?.includes(params.id) || !r.allowedUsers?.length)
        )

    const reportTypeIcon = (report: ReportAdminViewModel) => {
      if (report.schedule) {
        return 'icon-clock'
      } else if (isDocumentReportAdmin(report)) {
        return 'icon-excel'
      } else return 'icon-ondemand'
    }

    const hasPermissionByDefault = (report: ReportAdminViewModel) => {
      return report.allowedUsers?.length ? false : true
    }

    return reportAssignedToUser.length > 0 ? (
      reportAssignedToUser?.map((r) => (
        <li key={r.id}>
          <span>
            <i className={reportTypeIcon(r)}></i>
            <a href={`/ReportsManagement/EditForm/${r.id}`}>{r.name}</a>
          </span>
          {sliders ? (
            <div className="switch">
              <input type="checkbox" name="Enabled" />
              <span className="slider round"></span>
            </div>
          ) : (
            <div className={`rightRep ${hasPermissionByDefault(r) ? 'locked' : ''}`}>
              {hasPermissionByDefault(r) && <i className="icon-roles" />}
              <i className="icon-check" />
            </div>
          )}
        </li>
      ))
    ) : (
      <span className="noReportText">{t('NO_RESULTS_FOUND')}</span>
    )
  }

  const validate = (user: UserInputModel, tenant?: TenantViewModel): tenant is TenantViewModel => {
    const newValidation: Partial<Record<keyof UserInputModel, string>> = {}
    if (!user.name) newValidation.name = t('EMAIL_IS_REQUIRED')
    else if (!/\S+@\S+\.\S+/.test(user.name)) newValidation.name = t('INVALID_EMAIL')

    if (!user.firstName) newValidation.firstName = t('FIRST_NAME_IS_REQUIRED')
    if (!user.lastName) newValidation.lastName = t('LAST_NAME_IS_REQUIRED')
    if (!userTenant) newValidation.tenants = t('TENANT_NAME_IS_REQUIRED')
    setValidation(newValidation)
    return !Object.values(newValidation).length
  }

  const confirmUserDelete = async (confirm: boolean) => {
    if (!confirmDelete || !confirm) return

    deleteUser({
      tenantId: confirmDelete.tenantId,
      userId: confirmDelete.id,
    })
      .unwrap()
      .then(() => {
        setDeleteAlert({ title: t('SUCESS'), message: t('ITEM_DELETED') })
      })
      .catch(() => {
        setDeleteAlert({ title: t('CONFLICT'), message: t('ITEM_DELETE_FAILED') })
      })

    setConfirmDelete(undefined)
  }
  function trimWhitespace(dirty: UserInputModel) {
    const clean: UserInputModel = {
      ...dirty,
      name: dirty.name?.trim(),
      firstName: dirty.firstName?.trim(),
      lastName: dirty.lastName?.trim(),
      externalReference: dirty.externalReference?.trim(),
    }
    return clean
  }
  const submit = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    const trimmedUser = trimWhitespace(editingUser)

    if (!validate(trimmedUser, userTenant)) return
    if (!isNewUser && !isDuplicate) {
      putUser({ userId: params.id, tenantId: userTenant.id, model: trimmedUser })
        .unwrap()
        .then(async () => {
          if (channelsChanged && currentUser?.isAdmin) {
            const model = buildSettingsInputModel(channels, userNotificationsSettings)
            if (model) await putNotificationSettings({ id: params.id, model })
          }
          history.push('/UserManagement/users')
        })
        .catch((e) => {
          setSaveError(e && e.status === 409)
        })
    } else {
      postUser({ tenantId: userTenant.id, model: trimmedUser })
        .unwrap()
        .then(() => {
          history.push('/UserManagement/users')
        })
        .catch((e) => {
          setSaveError(e && e.status === 409)
        })
    }
  }

  return (
    <>
      <ul className="breadcrumbs">
        <li>
          <NavLink to="/UserManagement">
            <span>{t('ADMIN_PANEL')}</span>
          </NavLink>
        </li>
        {!!userTenant && (
          <li>
            <NavLink to={`/UserManagement/users/`}>
              <span>{userTenant.name}</span>
            </NavLink>
          </li>
        )}
        {!!editingUser && (
          <li>
            <span>
              {editingUser.firstName} {editingUser.lastName}
            </span>
          </li>
        )}
        <li>
          <span style={{ paddingRight: 0 }}>
            {(currentUser?.isAdmin || (currentUser?.isInternalUser && currentUser?.isManager)) && (
              <div className="btnArea">
                {userEdited && (
                  <a
                    onClick={() => {
                      setEditingUser(originalEditingUser)
                      setChannels(originalChannels)
                      setValidation({})
                    }}
                    className="btn archive"
                  >
                    <i className="icon-close" /> <label>{t('CANCEL')}</label>
                  </a>
                )}
                {!userEdited && (
                  <>
                    {params.tenant && !isNewUser && !isDuplicate && (
                      <a onClick={() => setConfirmDelete(existingUser.currentData)} className="btn btnClose">
                        <i className="icon-clear" /> <label>{t('DELETE')}</label>
                      </a>
                    )}
                    {!isDuplicate && !isNewUser && (
                      <NavLink
                        to={`/UserManagement/users/newUserEdit/${params.tenant}/${params.id}/duplicate`}
                        className="btn duplicate"
                      >
                        <i className="icon-duplicate" />
                        <label>{t('DUPLICATE')}</label>
                      </NavLink>
                    )}
                  </>
                )}
                <a className="btn btnOk" onClick={(e) => submit(e)}>
                  <i className="icon-save" /> <label>{t('SAVE')}</label>
                </a>
              </div>
            )}
          </span>
        </li>
      </ul>
      {(loading && (
        <div className="loading-state">
          <LoadingSpinner />
        </div>
      )) || (
        <form
          className="userEditpage adminArea"
          onSubmit={(e) => {
            e.preventDefault()
            return false
          }}
        >
          {userEdited ? (
            <div className="row">
              <div className="col-12 pendingChanges center">
                <div className="box">{t('PENDING_CHANGES_NOT_SAVED')}</div>
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className="row">
            <div className="itemList">
              <div
                className="col-12 col-sm-6 col-md-3"
                style={
                  currentUser?.isAdmin || (currentUser?.isInternalUser && currentUser?.isManager)
                    ? undefined
                    : { pointerEvents: 'none' }
                }
              >
                <div className="box ">
                  <div className="itemTitle">
                    <span>
                      <i className="icon-groups"></i>
                      {t('GROUP')}
                    </span>
                  </div>
                  <div className="selectArea">
                    <select
                      value={userTenant?.id ?? ''}
                      onChange={(e) => setSelectedTenantId(e.target.value)}
                      disabled={!!params.tenant}
                    >
                      <option disabled value="">
                        {t('PLEASE_SELECT_A_VALUE')}
                      </option>
                      {tenants?.map((obj) => (
                        <option key={obj.id} value={obj.id}>
                          {obj.name}
                        </option>
                      ))}
                    </select>
                    <i className="icon-bottom"></i>
                  </div>
                  <span className={validation.tenants ? 'validation-msg' : 'validation-msg-hidden'}>
                    {t('TENANT_NAME_IS_REQUIRED')}
                  </span>
                </div>
              </div>
            </div>
            <div className="itemList">
              <div
                className="col-12 col-sm-6 col-md-3"
                style={
                  currentUser?.isAdmin || (currentUser?.isInternalUser && currentUser?.isManager)
                    ? undefined
                    : { pointerEvents: 'none' }
                }
              >
                <div className="box">
                  <div className="itemTitle">
                    <span>
                      <i className="icon-info"></i>
                      {t('USER_INFORMATION')}
                    </span>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <label>{t('FIRST_NAME')}*</label>
                      <div className="input-area">
                        <input
                          type="text"
                          name="FirstName"
                          autoComplete="none"
                          list="autocompleteOff"
                          value={editingUser.firstName ?? ''}
                          placeholder={validation.firstName}
                          onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                        />
                      </div>
                      <span className={validation.firstName ? 'validation-msg' : 'validation-msg-hidden'}>
                        {validation.firstName}
                      </span>
                    </div>
                    <div className="col-6">
                      <label>{t('MIDDLE_NAME')}</label>
                      <div className="input-area">
                        <input
                          name="MiddleName"
                          autoComplete="none"
                          list="autocompleteOff"
                          type="text"
                          value={editingUser.middleName ?? ''}
                          onChange={(e) => setEditingUser({ ...editingUser, middleName: e.target.value })}
                        />
                      </div>
                      <span className={validation.middleName ? 'validation-msg' : 'validation-msg-hidden'}>
                        {validation.middleName}
                      </span>
                    </div>
                    <div className="col-6">
                      <label>{t('LAST_NAME')}*</label>
                      <div className="input-area">
                        <input
                          name="LastName"
                          autoComplete="none"
                          list="autocompleteOff"
                          type="text"
                          value={editingUser.lastName ?? ''}
                          placeholder={validation.lastName}
                          onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                        />
                      </div>
                      <span className={validation.lastName ? 'validation-msg' : 'validation-msg-hidden'}>
                        {validation.lastName}
                      </span>
                    </div>
                    <div className="col-6">
                      <label>{t('EXTERNAL_REFERENCE')}</label>
                      <div className="input-area">
                        <input
                          name="ExternalReference"
                          autoComplete="none"
                          list="autocompleteOff"
                          type="text"
                          value={editingUser.externalReference ?? ''}
                          onChange={(e) => setEditingUser({ ...editingUser, externalReference: e.target.value })}
                        />
                      </div>
                      <span className={validation.externalReference ? 'validation-msg' : 'validation-msg-hidden'}>
                        {validation.externalReference}
                      </span>
                    </div>
                    <div className="col-12">
                      <div className="flex" style={{ alignItems: 'end', gridGap: '30px' }}>
                        <div className="emailArea">
                          <label>{t('EMAIL')}*</label>
                          <div className="input-area">
                            <input
                              name="Name"
                              autoComplete="none"
                              list="autocompleteOff"
                              type="text"
                              value={editingUser.name}
                              onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                              placeholder={validation.name}
                            />
                          </div>
                          <span className={validation.name ? 'validation-msg' : 'validation-msg-hidden'}>
                            {validation.name}
                          </span>
                        </div>
                        {!isNewUser && !isDuplicate && (
                          <div className="idCopy">
                            <i
                              style={
                                currentUser?.isAdmin || (currentUser?.isInternalUser && currentUser?.isManager)
                                  ? { cursor: 'pointer' }
                                  : undefined
                              }
                              onClick={() => copyToClipboard(params.id)}
                              className="icon-id"
                            ></i>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <RolesAndPrivilegies
                currentUser={currentUser}
                userTenant={userTenant}
                existingUser={existingUser.currentData}
                editingUser={editingUser}
                toggleGuestUserRole={toggleGuestUserRole}
                toggleManagerRole={toggleManager}
                togglePermission={togglePermission}
                setEditingUser={setEditingUser}
              ></RolesAndPrivilegies>

              {!isNewUser && !!userNotificationsSettings && !isDuplicate && (
                <NotificationSettings handleSettingsUpdate={setChannels} userToEdit={editingUser} channels={channels} />
              )}

              <div className="col-12 col-sm-6 col-md-3">
                {editingUser.permissions && editingUser.permissions.includes(PermissionsEnum.ReportsView) && (
                  <div className="box">
                    <div className="itemTitle">
                      <span>
                        <i className="icon-report"></i>
                        {t('REPORTS')}
                      </span>
                    </div>
                    <ul className="reportsList">{renderUserReports()}</ul>
                  </div>
                )}
              </div>
              <div className="col-12 col-sm-3 col-md-3">
                <div className="box">
                  <div className="itemTitle">
                    <span>
                      <i className="icon-tag"></i>
                      {t('TAGS')}
                    </span>
                  </div>
                  <div className="user-tags">
                    <div className="input-area">
                      <input
                        name={`fieldnameveryunique${new Date().getTime()}`}
                        type="text"
                        autoComplete="none"
                        list="autocompleteOff"
                        value={editingTag}
                        onChange={(e) => setEditingTag(e.target.value)}
                      />{' '}
                    </div>
                    <button className="btnW" onClick={() => addTag(editingTag)} disabled={!editingTag}>
                      <i className="icon-add" /> tag
                    </button>
                    {!!editingTag &&
                      userTags.currentData?.some((t) => t.toLowerCase().includes(editingTag.toLowerCase())) && (
                        <ul className="tag-autocomplete tagList">
                          {userTags.currentData
                            .filter((t) => t.toLowerCase().includes(editingTag.toLowerCase()) && t !== editingTag)
                            .map((t, idx) => (
                              <li key={idx} onClick={() => setEditingTag(t)}>
                                {t}
                              </li>
                            ))}
                        </ul>
                      )}
                  </div>
                  {!!editingUser.tags?.length && (
                    <ul className="tagList">
                      {editingUser.tags?.map((tag) => (
                        <li key={tag} className="tagInUl">
                          <i
                            className="icon-close"
                            onClick={() =>
                              setEditingUser({ ...editingUser, tags: editingUser.tags?.filter((t) => t !== tag) })
                            }
                          />
                          <span>{tag}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-9 col-md-9 unitsArea assignUnitsOn">
                {assignmentMode === 'tenants' && (
                  <AssignTenants editingUser={editingUser} userTenant={userTenant} onChange={setEditingUser} />
                )}
                {assignmentMode === 'customers' && (
                  <AssignCustomers editingUser={editingUser} userTenant={userTenant} onChange={setEditingUser} />
                )}
              </div>
            </div>
          </div>
        </form>
      )}
      <Confirm
        visible={!!confirmDelete}
        onClose={confirmUserDelete}
        title={t('PLEASE_CONFIRM')}
        message={`${t('DO_YOU_WANT_TO_DELETE')} ${editingUser?.name}?`}
      />
      {deleteAlert && (
        <Alert
          onClose={() => {
            setDeleteAlert(undefined)
            if (!deleteUserResult.isError) history.goBack()
          }}
          visible={true}
          title={deleteAlert.title}
          message={deleteAlert.message}
        />
      )}
      <Alert
        onClose={() => setSaveError(false)}
        visible={saveError}
        title={t('API_DATA_VALIDATION_ERROR_TITLE')}
        message={t('API_DATA_VALIDATION_ERROR_MSG')}
      />
      {idCopied && <IdIsCopied idCopied={true} />}
    </>
  )
}
